import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";
import { getKycProgramById } from "../../service/api";
import CountryAutocomplete from "../../components/country-autocomplete";

interface WalletProgram {
  id: number;
  wallet_type: "Private Wallet" | "Business Wallet";
  default_currency: string;
  disable_address_fields_in_registration_form: number;
  enable_questionnaire_individual_lvl1: boolean;
  enable_questionnaire_corporate_lvl1: boolean;
  enable_questionnaire_corporate_edd: boolean;
}

const CompanyInformationForm1 = () => {
  const navigate = useNavigate();
  const newUser = useAppSelector((state) => state.register.newUser);
  const [walletProgram, setWalletProgram] = useState<WalletProgram>();

  if (!newUser) {
    navigate("/signin");
  }

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        if (newUser?.kyc_program_id) {
          const data = await getKycProgramById(newUser.kyc_program_id);
          setWalletProgram(data.program);
        }
      } catch (error) {
        console.error("Error getting program:", error);
      }
    };
    fetchProgram();
  }, [newUser?.kyc_program_id]);

  const initialValues = {
    legalName: "",
    tradingName: "",
    registrationNumber: "",
    dateOfIncorporation: "",
    businessIndustry: "",
    leiNumber: "",
    websiteLink: "",
    annualTurnover: "",
    taxNumber: "",
    howLongTrading: "",
    issueBearerShares: "",
    areCashTransactions: "",
    cashTransactionsPercentage: 0,

    areYouRegulated: "",
    regulatedJurisdiction: "",
    licenseTypeAndNumber: "",
    linkToLicense: "",

    servicesRequired: "",

    parentName: "",
    parentIndustry: "",
    parentAddress: {
      country: "",
      address: "",
      zip: "",
    },
    parentWebsiteLink: "",

    howDidYouHear: "",
  };

  const validationSchema = Yup.object().shape({
    legalName: Yup.string().required("Required"),
    tradingName: Yup.string().required("Required"),
    registrationNumber: Yup.string().required("Required"),
    dateOfIncorporation: Yup.string().required("Required"),
    businessIndustry: Yup.string().required("Required"),
    leiNumber: Yup.string().required("Required"),
    websiteLink: Yup.string().url("Must be a valid URL").required("Required"),
    annualTurnover: Yup.string().required("Required"),
    taxNumber: Yup.string().required("Required"),
    howLongTrading: Yup.string().required("Required"),
    issueBearerShares: Yup.string().required("Please select Yes or No"),
    areCashTransactions: Yup.string().required("Please select Yes or No"),
    cashTransactionsPercentage: Yup.number().when("areCashTransactions", {
      is: "yes",
      then: Yup.number().required("Required if you have cash transactions"),
      otherwise: Yup.number(),
    }),

    areYouRegulated: Yup.string().required("Please select Yes or No"),
    regulatedJurisdiction: Yup.string(),
    licenseTypeAndNumber: Yup.string(),
    linkToLicense: Yup.string()
      .url("Must be a valid URL")
      .when("areYouRegulated", {
        is: "yes",
        then: Yup.string().required(
          "Link to license is required when regulated"
        ),
        otherwise: Yup.string().nullable(true),
      }),

    servicesRequired: Yup.string().required("Required"),

    parentName: Yup.string(),
    parentIndustry: Yup.string(),
    parentAddress: Yup.object().shape({
      country: Yup.string(),
      address: Yup.string(),
      zip: Yup.string(),
    }),
    parentWebsiteLink: Yup.string().url("Must be a valid URL").nullable(true),

    howDidYouHear: Yup.string().required("Required"),
  });

  const onSubmit = (values: any) => {
    localStorage.setItem("form3_json", JSON.stringify(values));
    if (
      values.areYouRegulated === "yes" &&
      Number(walletProgram?.enable_questionnaire_corporate_edd) === 1
    ) {
      navigate("/edd-ques");
    } else {
      navigate("/corp-info-2");
    }
  };

  const { handleSubmit, getFieldProps, getFieldMeta, isSubmitting, values } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit,
    });

  const errorText = (fieldName: keyof typeof initialValues) => {
    const meta = getFieldMeta(fieldName);
    return meta.touched && meta.error ? meta.error : "";
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <CardContent>
              <Stack spacing={4}>
                <Typography variant="h6" mt={2}>
                  Company Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Legal Name"
                      fullWidth
                      {...getFieldProps("legalName")}
                      error={!!errorText("legalName")}
                      helperText={errorText("legalName")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Trading Name"
                      fullWidth
                      {...getFieldProps("tradingName")}
                      error={!!errorText("tradingName")}
                      helperText={errorText("tradingName")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Registration Number"
                      fullWidth
                      {...getFieldProps("registrationNumber")}
                      error={!!errorText("registrationNumber")}
                      helperText={errorText("registrationNumber")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date of Incorporation"
                      fullWidth
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      {...getFieldProps("dateOfIncorporation")}
                      error={!!errorText("dateOfIncorporation")}
                      helperText={errorText("dateOfIncorporation")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Business Industry"
                      fullWidth
                      {...getFieldProps("businessIndustry")}
                      error={!!errorText("businessIndustry")}
                      helperText={errorText("businessIndustry")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Legal Entity Identifier (LEI)"
                      fullWidth
                      {...getFieldProps("leiNumber")}
                      error={!!errorText("leiNumber")}
                      helperText={errorText("leiNumber")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Website Link"
                      fullWidth
                      {...getFieldProps("websiteLink")}
                      error={!!errorText("websiteLink")}
                      helperText={errorText("websiteLink")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company Annual Turnover"
                      fullWidth
                      {...getFieldProps("annualTurnover")}
                      error={!!errorText("annualTurnover")}
                      helperText={errorText("annualTurnover")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Tax Number"
                      fullWidth
                      {...getFieldProps("taxNumber")}
                      error={!!errorText("taxNumber")}
                      helperText={errorText("taxNumber")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="How long has the business been trading?"
                      fullWidth
                      {...getFieldProps("howLongTrading")}
                      error={!!errorText("howLongTrading")}
                      helperText={errorText("howLongTrading")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Do you issue bearer shares?</FormLabel>
                      <RadioGroup row {...getFieldProps("issueBearerShares")}>
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                      {errorText("issueBearerShares") && (
                        <Typography variant="caption" color="error">
                          {errorText("issueBearerShares")}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>
                        Are cash transactions involved in your business model?
                      </FormLabel>
                      <RadioGroup row {...getFieldProps("areCashTransactions")}>
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                      {errorText("areCashTransactions") && (
                        <Typography variant="caption" color="error">
                          {errorText("areCashTransactions")}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  {values.areCashTransactions === "yes" && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="If yes, what % of your transactions are in cash?"
                        fullWidth
                        type="number"
                        {...getFieldProps("cashTransactionsPercentage")}
                        error={!!errorText("cashTransactionsPercentage")}
                        helperText={errorText("cashTransactionsPercentage")}
                      />
                    </Grid>
                  )}
                </Grid>

                <Typography variant="h6" mt={2}>
                  Licensing
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Are you regulated?</FormLabel>
                      <RadioGroup row {...getFieldProps("areYouRegulated")}>
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                      {errorText("areYouRegulated") && (
                        <Typography variant="caption" color="error">
                          {errorText("areYouRegulated")}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  {values.areYouRegulated === "yes" && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          label="In which jurisdiction are you regulated?"
                          fullWidth
                          {...getFieldProps("regulatedJurisdiction")}
                          error={!!errorText("regulatedJurisdiction")}
                          helperText={errorText("regulatedJurisdiction")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Type and number of your license"
                          fullWidth
                          {...getFieldProps("licenseTypeAndNumber")}
                          error={!!errorText("licenseTypeAndNumber")}
                          helperText={errorText("licenseTypeAndNumber")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Link to license (if any)"
                          fullWidth
                          {...getFieldProps("linkToLicense")}
                          error={!!errorText("linkToLicense")}
                          helperText={errorText("linkToLicense")}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Typography variant="h6" mt={2}>
                  Services Required
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel>Which services do you need?</FormLabel>
                      <RadioGroup row {...getFieldProps("servicesRequired")}>
                        <FormControlLabel
                          value="exchange"
                          control={<Radio />}
                          label="Exchange"
                        />
                        <FormControlLabel
                          value="wallet"
                          control={<Radio />}
                          label="Wallet"
                        />
                        <FormControlLabel
                          value="both"
                          control={<Radio />}
                          label="Both"
                        />
                      </RadioGroup>
                      {errorText("servicesRequired") && (
                        <Typography variant="caption" color="error">
                          {errorText("servicesRequired")}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                <Typography variant="h6" mt={2}>
                  Parent company Information (if applicable)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Parent company name"
                      fullWidth
                      {...getFieldProps("parentName")}
                      error={!!errorText("parentName")}
                      helperText={errorText("parentName")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Industry"
                      fullWidth
                      {...getFieldProps("parentIndustry")}
                      error={!!errorText("parentIndustry")}
                      helperText={errorText("parentIndustry")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CountryAutocomplete
                      label="Country"
                      {...getFieldProps("parentAddress.country")}
                      value={values.parentAddress.country || ""}
                      error={
                        getFieldMeta("parentAddress.country").touched
                          ? getFieldMeta("parentAddress.country").error
                          : undefined
                      }
                    />
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Address"
                      fullWidth
                      {...getFieldProps("parentAddress.address")}
                      error={
                        !!getFieldMeta("parentAddress.address").touched &&
                        !!getFieldMeta("parentAddress.address").error
                      }
                      helperText={
                        getFieldMeta("parentAddress.address").touched
                          ? getFieldMeta("parentAddress.address").error
                          : ""
                      }
                    />
                  </Grid>

                  {/* ZIP Code */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="ZIP Code"
                      fullWidth
                      {...getFieldProps("parentAddress.zip")}
                      error={
                        !!getFieldMeta("parentAddress.zip").touched &&
                        !!getFieldMeta("parentAddress.zip").error
                      }
                      helperText={
                        getFieldMeta("parentAddress.zip").touched
                          ? getFieldMeta("parentAddress.zip").error
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Website link"
                      fullWidth
                      {...getFieldProps("parentWebsiteLink")}
                      error={!!errorText("parentWebsiteLink")}
                      helperText={errorText("parentWebsiteLink")}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" mt={2}>
                  How did you hear about us?
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Please specify"
                      fullWidth
                      {...getFieldProps("howDidYouHear")}
                      error={!!errorText("howDidYouHear")}
                      helperText={errorText("howDidYouHear")}
                    />
                  </Grid>
                </Grid>

                <Stack spacing={2} mt={3}>
                  <LoadingButton
                    fullWidth
                    loading={isSubmitting}
                    variant="contained"
                    type="submit"
                  >
                    Save & Continue
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default CompanyInformationForm1;
